import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { format, parseISO } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import {
  getPendingTimeOff,
  approveTimeOff,
  denyTimeOff,
  TimeOffRequest,
} from '@/store/slices/timeOffSlice';
import { colors, font, space, radius, shadow } from '@/theme/chalkCourt';

const fmtDate = (iso: string): string => {
  try {
    return format(parseISO(iso), 'MMM d');
  } catch {
    return iso;
  }
};

/**
 * Manager approvals — the pending time-off queue with in-app Approve / Deny.
 * Backed by `/timeoff/pending` + `/timeoff/{id}/approve|deny` (admin-gated:
 * EMPLOYEE.IS_ADMIN). Reached only from admin-gated entry points; the thunk
 * also self-gates and 403-degrades, so a non-admin sees an empty, safe state.
 */
const ApprovalsScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { pendingRequests, isApprover, error } = useSelector((state: RootState) => state.timeOff);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  );

  const load = useCallback(async () => {
    setLoading(true);
    await dispatch(getPendingTimeOff());
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!feedback) return undefined;
    const id = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(id);
  }, [feedback]);

  const decide = async (req: TimeOffRequest, approve: boolean) => {
    if (actingId) return;
    setActingId(req.id);
    try {
      const res = await (approve
        ? dispatch(approveTimeOff(req.id))
        : dispatch(denyTimeOff(req.id))
      ).unwrap();
      if (res && res.success === false) {
        setFeedback({ type: 'error', text: res.message || 'Could not complete that action.' });
      } else {
        const who = req.employeeName ?? 'Request';
        setFeedback({
          type: 'success',
          text: `${approve ? 'Approved' : 'Denied'} — ${who} · ${fmtDate(req.start)}–${fmtDate(req.end)}`,
        });
        await dispatch(getPendingTimeOff());
      }
    } catch (e) {
      setFeedback({ type: 'error', text: typeof e === 'string' ? e : 'Could not complete that action.' });
    } finally {
      setActingId(null);
    }
  };

  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.content}
      refreshControl={
        <RefreshControl
          refreshing={loading}
          onRefresh={load}
          tintColor={colors.accent}
          colors={[colors.accent]}
        />
      }>
      {feedback && (
        <View
          style={[
            styles.banner,
            feedback.type === 'success' ? styles.bannerSuccess : styles.bannerError,
          ]}>
          <Icon
            name={feedback.type === 'success' ? 'checkmark-circle' : 'alert-circle'}
            size={18}
            color={feedback.type === 'success' ? '#137333' : '#c5221f'}
          />
          <Text
            style={[
              styles.bannerText,
              { color: feedback.type === 'success' ? '#137333' : '#c5221f' },
            ]}>
            {feedback.text}
          </Text>
        </View>
      )}

      <View style={styles.header}>
        <Text style={styles.kicker}>TIME OFF</Text>
        <Text style={styles.headline}>APPROVALS</Text>
        <View style={styles.rule} />
        <Text style={styles.subtitle}>
          {pendingRequests.length} pending request{pendingRequests.length === 1 ? '' : 's'}
        </Text>
      </View>

      {loading && !isApprover ? (
        <View style={styles.empty}>
          <ActivityIndicator color={colors.accent} />
        </View>
      ) : error ? (
        // A non-403 failure (network/5xx) — the queue couldn't load. Distinct from
        // the genuine no-access case below (403 → isApprover:false, no error). Retryable.
        <View style={styles.empty}>
          <Icon name="cloud-offline-outline" size={28} color={colors.inkFaint} />
          <Text style={styles.emptyTitle}>Couldn't load approvals</Text>
          <Text style={styles.emptyBody}>{error}</Text>
          <TouchableOpacity style={styles.retryBtn} activeOpacity={0.8} onPress={load}>
            <Icon name="refresh" size={16} color={colors.onAccent} />
            <Text style={styles.retryText}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : !isApprover ? (
        <View style={styles.empty}>
          <Icon name="lock-closed-outline" size={28} color={colors.inkFaint} />
          <Text style={styles.emptyBody}>You don't have approval access.</Text>
        </View>
      ) : pendingRequests.length === 0 ? (
        <View style={styles.empty}>
          <View style={styles.emptyMark}>
            <Icon name="checkmark-done" size={30} color={colors.court} />
          </View>
          <Text style={styles.emptyTitle}>All caught up</Text>
          <Text style={styles.emptyBody}>No time-off requests are waiting on you.</Text>
        </View>
      ) : (
        pendingRequests.map((req) => {
          const busy = actingId === req.id;
          return (
            <View key={req.id} style={styles.card}>
              <View style={styles.cardHead}>
                <Text style={styles.cardName}>{req.employeeName ?? `Employee ${req.employeeId}`}</Text>
                <Text style={styles.cardDays}>
                  {req.days} day{req.days === 1 ? '' : 's'}
                </Text>
              </View>
              <Text style={styles.cardDates}>
                {fmtDate(req.start)} – {fmtDate(req.end)}
              </Text>
              {!!req.notes && <Text style={styles.cardNotes}>“{req.notes}”</Text>}
              <View style={styles.actions}>
                <TouchableOpacity
                  style={[styles.btn, styles.denyBtn, busy && styles.btnDisabled]}
                  activeOpacity={0.8}
                  disabled={!!actingId}
                  onPress={() => decide(req, false)}>
                  {busy ? (
                    <ActivityIndicator size="small" color={colors.accentDark} />
                  ) : (
                    <>
                      <Icon name="close" size={16} color={colors.accentDark} />
                      <Text style={styles.denyText}>Deny</Text>
                    </>
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.btn, styles.approveBtn, busy && styles.btnDisabled]}
                  activeOpacity={0.8}
                  disabled={!!actingId}
                  onPress={() => decide(req, true)}>
                  {busy ? (
                    <ActivityIndicator size="small" color={colors.onAccent} />
                  ) : (
                    <>
                      <Icon name="checkmark" size={16} color={colors.onAccent} />
                      <Text style={styles.approveText}>Approve</Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          );
        })
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  content: { padding: space.lg, paddingBottom: space.xxl },

  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    marginBottom: space.md,
  },
  bannerSuccess: { backgroundColor: '#e6f4ea', borderWidth: 1, borderColor: '#b7dfc2' },
  bannerError: { backgroundColor: '#fce8e6', borderWidth: 1, borderColor: '#f3c1bd' },
  bannerText: { flex: 1, fontSize: 14, fontFamily: font.bodySemi },

  header: { marginBottom: space.lg },
  kicker: { fontFamily: font.displayMed, fontSize: 13, letterSpacing: 1.5, color: colors.inkFaint },
  headline: { fontFamily: font.display, fontSize: 40, lineHeight: 44, color: colors.ink },
  rule: { height: 2, backgroundColor: colors.lineStrong, width: 44, marginVertical: space.md },
  subtitle: { fontFamily: font.body, fontSize: 14, color: colors.inkSoft },

  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.md,
    borderWidth: 1,
    borderColor: colors.line,
    ...shadow.card,
  },
  cardHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  cardName: { fontFamily: font.bodyBold, fontSize: 16, color: colors.ink, flex: 1 },
  cardDays: { fontFamily: font.displayMed, fontSize: 15, color: colors.accent },
  cardDates: { fontFamily: font.bodyMed, fontSize: 14, color: colors.inkSoft },
  cardNotes: {
    fontFamily: font.body,
    fontSize: 14,
    color: colors.inkSoft,
    fontStyle: 'italic',
    marginTop: space.sm,
  },
  actions: { flexDirection: 'row', gap: space.sm, marginTop: space.md },
  btn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 11,
    borderRadius: radius.md,
  },
  btnDisabled: { opacity: 0.6 },
  denyBtn: { backgroundColor: colors.accentSoft },
  denyText: { fontFamily: font.bodySemi, fontSize: 15, color: colors.accentDark },
  approveBtn: { backgroundColor: colors.accent },
  approveText: { fontFamily: font.bodySemi, fontSize: 15, color: colors.onAccent },

  empty: { alignItems: 'center', paddingVertical: space.xxl },
  emptyMark: {
    width: 60,
    height: 60,
    borderRadius: radius.pill,
    backgroundColor: colors.courtSoft,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: space.md,
  },
  emptyTitle: { fontFamily: font.bodyBold, fontSize: 16, color: colors.ink, marginBottom: 4 },
  emptyBody: {
    fontFamily: font.body,
    fontSize: 14,
    color: colors.inkFaint,
    textAlign: 'center',
    marginTop: space.sm,
    paddingHorizontal: space.xl,
  },
  retryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: space.lg,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: radius.md,
    backgroundColor: colors.accent,
  },
  retryText: { fontFamily: font.bodySemi, fontSize: 15, color: colors.onAccent },
});

export default ApprovalsScreen;
